import React, { useEffect, useRef, useState } from "react";
import { api, money, formatApiError, BACKEND_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Truck, Fuel, Calendar, Clock, X, Eye, EyeOff, Upload } from "lucide-react";
import GasCylinderIcon from "../../components/icons/GasCylinderIcon";
import { useConfirm } from "../../components/ConfirmProvider";

/**
 * Bloque para programar un cambio de precio futuro. Solo se permite UNA
 * programación pendiente por producto. Cuando llega la fecha, el backend
 * aplica el cambio y limpia los campos en automático.
 */
function ScheduledPriceBlock({ scheduledPrice, scheduledAt, currentPrice, onChange, editing }) {
  const hasScheduled = !!scheduledAt;
  // Convertir ISO UTC ↔ valor para `<input type="datetime-local">` (sin zona).
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const fromLocalInput = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };
  const minLocal = (() => {
    const d = new Date(Date.now() + 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const fmtPretty = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("es-MX", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    } catch { return iso; }
  };

  if (!hasScheduled) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 p-3" data-testid="schedule-price-block">
        <button
          type="button"
          className="w-full text-left flex items-center gap-2 text-sm font-semibold text-neutral-700 hover:text-neutral-900"
          onClick={() => {
            // 1h en el futuro como sugerencia inicial; el precio nuevo
            // arranca igual al precio actual para que el admin solo
            // ajuste lo que va a cambiar.
            const future = new Date(Date.now() + 60 * 60 * 1000).toISOString();
            const startPrice = currentPrice === "" || currentPrice === null || currentPrice === undefined
              ? 0
              : Number(currentPrice) || 0;
            onChange(startPrice, future);
          }}
          data-testid="schedule-price-add-btn"
        >
          <Calendar size={16} /> Programar cambio de precio
        </button>
        {editing && (
          <p className="text-[11px] text-neutral-500 mt-1">
            Define un precio nuevo y la fecha+hora exacta en que entrará en vigor. Al llegar el momento, el cambio se aplica automáticamente.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/50 p-4 space-y-3" data-testid="schedule-price-block-active">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <Calendar size={16} /> Cambio de precio programado
        </div>
        <button
          type="button"
          className="text-xs font-medium text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"
          onClick={() => onChange(null, null)}
          data-testid="schedule-price-cancel-btn"
        >
          <X size={12} /> Quitar
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <div>
          <Label className="text-xs flex items-center gap-1 h-4">Nuevo precio</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            className="h-11 mt-1"
            value={scheduledPrice ?? ""}
            placeholder="0.00"
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value), scheduledAt)}
            data-testid="schedule-price-input"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1 h-4"><Clock size={12} /> Fecha y hora</Label>
          <Input
            type="datetime-local"
            className="h-11 mt-1"
            value={toLocalInput(scheduledAt)}
            min={minLocal}
            onChange={(e) => onChange(scheduledPrice, fromLocalInput(e.target.value))}
            data-testid="schedule-price-at-input"
          />
        </div>
      </div>
      {scheduledAt && (
        <p className="text-[11px] text-amber-900">
          Se aplicará el <strong>{fmtPretty(scheduledAt)}</strong>{scheduledPrice ? ` a $${Number(scheduledPrice).toFixed(2)}` : ""}.
        </p>
      )}
    </div>
  );
}

const EMPTY = {
  kind: "cylinder",
  channel: "cilindrera",
  name: "",
  size_kg: 20,
  unit: "liter",
  price: 0,
  image_url: "",
  active: true,
  scheduled_price: null,
  scheduled_price_at: null,
};

const CHANNELS = [
  { v: "autotanque", label: "Autotanque", Icon: Truck },
  { v: "cilindrera", label: "Cilindrera", Icon: GasCylinderIcon },
  { v: "estacion", label: "Estación", Icon: Fuel },
];

const channelMeta = (c) => CHANNELS.find((x) => x.v === c) || CHANNELS[1];

export default function AdminProducts() {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  // Vista por canal: null = pantalla de tarjetas; "cilindrera"/"autotanque"/"estacion"
  // = lista de productos filtrados por ese canal.
  const [selectedChannel, setSelectedChannel] = useState(null);
  // Configuración de la empresa (para imágenes de canal y visibilidad).
  const [company, setCompany] = useState(null);
  // Estado del sheet "Editar canal" (image_url + visible toggle).
  const [channelEdit, setChannelEdit] = useState(null); // { v, kindKey, image_url, hidden }
  const [savingChannel, setSavingChannel] = useState(false);
  const [uploadingChannelImg, setUploadingChannelImg] = useState(false);
  const channelImgInputRef = useRef(null);

  const loadCompany = () => api.get("/companies/mine").then((r) => setCompany(r.data)).catch(() => {});

  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen supera 3MB");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // El backend devuelve url relativa (/api/files/...); para previsualizar
      // y guardar de forma estable usamos la URL absoluta con BACKEND_URL.
      const fullUrl = `${BACKEND_URL}${data.url}`;
      setForm((prev) => ({ ...prev, image_url: fullUrl }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const load = () => api.get("/products/admin").then((r) => setList(r.data));
  useEffect(() => { load(); loadCompany(); }, []);

  // Mapeo canal ↔ campos de empresa.
  const CHANNEL_FIELD_MAP = {
    cilindrera:  { kind: "cylinder",     hero: "hero_image_cylinder",  defaultFlag: "hero_default_cylinder",  hidden: "channel_hidden_cylinder" },
    autotanque:  { kind: "bulk",         hero: "hero_image_bulk",      defaultFlag: "hero_default_bulk",      hidden: "channel_hidden_bulk" },
    estacion:    { kind: "estacionario", hero: "hero_image_station",   defaultFlag: "hero_default_station",   hidden: "channel_hidden_station" },
  };

  const productsInChannel = (chan) => list.filter((p) => p.channel === chan);

  const openChannelEdit = (chan) => {
    const cm = channelMeta(chan);
    const fields = CHANNEL_FIELD_MAP[chan];
    setChannelEdit({
      v: chan,
      label: cm.label,
      kindKey: fields.kind,
      image_url: company?.[fields.hero] || "",
      hidden: !!company?.[fields.hidden],
    });
  };

  const handleChannelImgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen");
      if (channelImgInputRef.current) channelImgInputRef.current.value = "";
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen supera 3MB");
      if (channelImgInputRef.current) channelImgInputRef.current.value = "";
      return;
    }
    setUploadingChannelImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      setChannelEdit((prev) => prev ? { ...prev, image_url: fullUrl } : prev);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setUploadingChannelImg(false);
      if (channelImgInputRef.current) channelImgInputRef.current.value = "";
    }
  };

  const saveChannel = async () => {
    if (!channelEdit || !company?.company_id) return;
    const fields = CHANNEL_FIELD_MAP[channelEdit.v];
    setSavingChannel(true);
    try {
      const payload = {
        [fields.hero]: channelEdit.image_url || null,
        // Si hay URL → respetar imagen; si vacía → forzar "default" (usa el icono).
        [fields.defaultFlag]: !channelEdit.image_url,
        [fields.hidden]: !!channelEdit.hidden,
      };
      await api.patch(`/companies/${company.company_id}`, payload);
      toast.success("Canal actualizado");
      setChannelEdit(null);
      loadCompany();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSavingChannel(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = async (p) => {
    // Refrescar la lista para asegurar que cualquier precio programado
    // ya vencido haya sido aplicado por el backend antes de abrir el form.
    let fresh = p;
    try {
      const { data } = await api.get("/products/admin");
      const found = data.find((x) => x.product_id === p.product_id);
      if (found) fresh = found;
      setList(data);
    } catch { /* offline → use cached p */ }
    setEditingId(fresh.product_id);
    setForm({
      kind: fresh.kind || "cylinder",
      channel: fresh.channel || (fresh.kind === "bulk" ? "autotanque" : "cilindrera"),
      name: fresh.name || "",
      size_kg: fresh.size_kg ?? 20,
      unit: fresh.unit || "liter",
      price: fresh.price ?? 0,
      image_url: fresh.image_url || "",
      active: fresh.active !== false,
      stock: fresh.stock ?? null,
      scheduled_price: fresh.scheduled_price ?? null,
      scheduled_price_at: fresh.scheduled_price_at ?? null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    // Validar scheduled price si está activo
    if (form.scheduled_price_at) {
      const dt = new Date(form.scheduled_price_at);
      if (isNaN(dt.getTime())) { toast.error("Fecha y hora del precio programado inválida"); return; }
      if (dt.getTime() <= Date.now()) {
        toast.error("La fecha del precio programado debe ser futura");
        return;
      }
      if (form.scheduled_price === null || form.scheduled_price === "" || Number(form.scheduled_price) <= 0) {
        toast.error("Define el nuevo precio programado");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        price: Number(form.price) || 0,
        size_kg: form.kind === "cylinder" ? Number(form.size_kg) || null : null,
        unit: form.kind === "bulk" ? form.unit : null,
        scheduled_price: form.scheduled_price_at ? Number(form.scheduled_price) || null : null,
        scheduled_price_at: form.scheduled_price_at || null,
      };
      if (editingId) {
        await api.patch(`/products/${editingId}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      setForm(EMPTY);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    const ok = await confirm({
      title: "¿Eliminar producto?",
      description: `Se eliminará "${p.name}". Los pedidos anteriores conservan su snapshot.`,
      confirmText: "Sí, eliminar",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/products/${p.product_id}`);
    load();
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            {selectedChannel ? (
              <button
                onClick={() => setSelectedChannel(null)}
                className="text-xs text-neutral-500 hover:text-neutral-800 underline-offset-2 hover:underline mb-1 inline-flex items-center gap-1"
                data-testid="back-to-channels-btn"
              >
                ← Volver a canales
              </button>
            ) : null}
            <h1 className="font-display text-4xl font-extrabold tracking-tight">
              {selectedChannel ? `Productos · ${channelMeta(selectedChannel).label}` : "Productos"}
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              {selectedChannel
                ? "Productos despachados desde este canal."
                : "Selecciona un canal para administrar sus productos."}
            </p>
          </div>
          {selectedChannel ? (
            <Button
              onClick={() => {
                // Al abrir el form desde un canal, prefijar ese canal.
                setEditingId(null);
                const ch = selectedChannel;
                const kind = CHANNEL_FIELD_MAP[ch]?.kind || "cylinder";
                setForm({ ...EMPTY, channel: ch, kind });
                setOpen(true);
              }}
              className="h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
              data-testid="add-product-btn"
            >
              <Plus size={16} className="mr-2" />Agregar producto
            </Button>
          ) : null}
        </div>

        {!selectedChannel && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="channel-cards">
            {CHANNELS.map((c) => {
              const fields = CHANNEL_FIELD_MAP[c.v];
              const imgUrl = company?.[fields.hero] || null;
              const hidden = !!company?.[fields.hidden];
              const count = productsInChannel(c.v).length;
              const Icon = c.Icon;
              return (
                <div
                  key={c.v}
                  className="group relative rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition cursor-pointer"
                  onClick={() => setSelectedChannel(c.v)}
                  data-testid={`channel-card-${c.v}`}
                >
                  <div className="aspect-[16/10] bg-neutral-100 relative">
                    {imgUrl ? (
                      <img src={imgUrl} alt={c.label} className="absolute inset-0 w-full h-full object-cover" />
                    ) : company?.logo_url ? (
                      <img src={company.logo_url} alt={c.label} className="absolute inset-0 w-full h-full object-contain p-8 bg-neutral-50" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-company/10 text-[color:var(--company-primary)]">
                        <Icon size={64} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />
                    {hidden && (
                      <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 bg-red-600 text-white"
                        data-testid={`channel-${c.v}-hidden-badge`}
                      >
                        <EyeOff size={11} />Oculto
                      </span>
                    )}
                    <div className="absolute bottom-3 left-3 right-3 text-white flex items-end justify-between gap-2">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest opacity-80">Canal</p>
                        <p className="font-display font-extrabold text-2xl leading-tight" data-testid={`channel-${c.v}-title`}>{c.label}</p>
                      </div>
                      <span className="rounded-full bg-white/95 text-neutral-900 text-xs font-semibold px-2 py-1" data-testid={`channel-${c.v}-count`}>
                        {count} {count === 1 ? "producto" : "productos"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-neutral-200">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openChannelEdit(c.v); }}
                      data-testid={`channel-${c.v}-edit-btn`}
                    >
                      <Pencil size={14} className="mr-1.5" />Editar
                    </Button>
                    <span className="text-xs text-neutral-500">Toca la tarjeta para ver productos →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedChannel && (
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {productsInChannel(selectedChannel).map((p) => {
            const cm = channelMeta(p.channel);
            const ChIcon = cm.Icon;
            return (
              <div
                key={p.product_id}
                className={`flex items-start gap-3 bg-white rounded-xl border p-4 transition ${
                  p.active === false
                    ? "border-neutral-200 opacity-60 grayscale"
                    : "border-neutral-200"
                }`}
                data-testid={`admin-product-${p.product_id}`}
              >
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="h-14 w-14 rounded-lg object-cover bg-neutral-100 shrink-0" />
                ) : (
                  <div className="h-14 w-14 rounded-lg bg-company/10 text-[color:var(--company-primary)] border border-company/20 flex items-center justify-center shrink-0">
                    <ChIcon size={22} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{p.name}</p>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-neutral-100 text-neutral-700 border border-neutral-200">
                      <ChIcon size={10} />{cm.label}
                    </span>
                    {p.active === false ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-red-50 text-red-700 border border-red-200"
                        title="Oculto para los clientes"
                        data-testid={`product-hidden-${p.product_id}`}
                      >
                        <EyeOff size={10} />Oculto
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200"
                        title="Visible para los clientes"
                        data-testid={`product-visible-${p.product_id}`}
                      >
                        <Eye size={10} />Visible
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    {p.kind === "cylinder" && `Cilindro • ${p.size_kg} kg`}
                    {p.kind === "bulk" && `A granel • por ${p.unit === "kg" ? "kilogramo" : "litro"}`}
                    {p.kind === "estacionario" && `Estacionario • recarga en estación`}
                  </p>
                  <p className="font-display font-extrabold text-lg mt-1">{money(p.price)}</p>
                  {p.scheduled_price_at && (
                    <p
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200"
                      title="Cambio de precio programado"
                      data-testid={`product-scheduled-${p.product_id}`}
                    >
                      <Calendar size={11} />
                      {money(p.scheduled_price)} · {(() => {
                        try {
                          return new Date(p.scheduled_price_at).toLocaleString("es-MX", {
                            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
                          });
                        } catch { return p.scheduled_price_at; }
                      })()}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(p)} data-testid={`delete-product-${p.product_id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
          {productsInChannel(selectedChannel).length === 0 && (
            <div className="md:col-span-2 bg-white rounded-2xl border border-dashed border-neutral-300 p-8 text-center">
              <p className="text-sm text-neutral-500">Aún no tienes productos en este canal. Agrega el primero para que los clientes puedan pedir.</p>
            </div>
          )}
        </div>
        )}

        <BottomSheet
          open={open}
          onOpenChange={setOpen}
          title={editingId ? "Editar producto" : "Nuevo producto"}
          description="Define el producto y el canal desde el que se despacha."
          testid="product-form-sheet"
        >
          <div className="flex flex-col h-[70vh]">
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 overflow-y-auto flex-1">
            <div className="space-y-4 min-h-full">
              {/* Channel → auto-determines product kind */}
              <div>
                <Label>Canal de distribución</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {CHANNELS.map((c) => {
                    const selected = form.channel === c.v;
                    const CIcon = c.Icon;
                    const kindFor = c.v === "autotanque" ? "bulk" : c.v === "cilindrera" ? "cylinder" : "estacionario";
                    return (
                      <button
                        key={c.v}
                        type="button"
                        disabled={!!editingId}
                        onClick={() => setForm({
                          ...form,
                          channel: c.v,
                          kind: kindFor,
                          // Reset channel-specific fields
                          size_kg: kindFor === "cylinder" ? (form.size_kg || 20) : null,
                          unit: kindFor === "bulk" ? (form.unit || "liter") : null,
                        })}
                        className={`rounded-xl border px-3 py-3 flex flex-col items-center gap-1 transition ${
                          selected
                            ? "border-company bg-company/10 text-[color:var(--company-primary)]"
                            : "border-neutral-200 hover:border-neutral-300 bg-white text-neutral-600"
                        } ${editingId && !selected ? "opacity-40 cursor-not-allowed hover:border-neutral-200" : ""}`}
                        data-testid={`product-channel-${c.v}`}
                      >
                        <CIcon size={20} />
                        <span className="text-xs font-semibold">{c.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">
                  {editingId && "El canal de un producto no puede cambiarse. "}
                  {form.kind === "bulk" && "Tipo: A granel (se vende por litro o kilogramo)"}
                  {form.kind === "cylinder" && "Tipo: Cilindro (se vende por unidad)"}
                  {form.kind === "estacionario" && "Tipo: Estacionario (se vende en estación, sin envío)"}
                </p>
              </div>

              <div>
                <Label>Nombre</Label>
                <Input
                  className="h-11 mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={form.kind === "cylinder" ? "Ej: Cilindro 20 kg" : form.kind === "bulk" ? "Ej: Gas a granel - Litros" : "Ej: Recarga en estación"}
                  data-testid="product-name"
                />
              </div>

              {form.kind === "cylinder" && (
                <div>
                  <Label>Tamaño</Label>
                  <Select value={String(form.size_kg)} onValueChange={(v) => setForm({ ...form, size_kg: Number(v) })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="product-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 kg</SelectItem>
                      <SelectItem value="15">15 kg</SelectItem>
                      <SelectItem value="20">20 kg</SelectItem>
                      <SelectItem value="25">25 kg</SelectItem>
                      <SelectItem value="30">30 kg</SelectItem>
                      <SelectItem value="35">35 kg</SelectItem>
                      <SelectItem value="40">40 kg</SelectItem>
                      <SelectItem value="45">45 kg</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.kind === "bulk" && (
                <div>
                  <Label>Unidad de venta</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="product-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="liter">Litro</SelectItem>
                      <SelectItem value="kg">Kilogramo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Precio</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="h-11 mt-1"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="product-price"
                />
              </div>

              {/* Programación de precio futuro */}
              <ScheduledPriceBlock
                scheduledPrice={form.scheduled_price}
                scheduledAt={form.scheduled_price_at}
                currentPrice={form.price}
                onChange={(price, at) => setForm({ ...form, scheduled_price: price, scheduled_price_at: at })}
                editing={!!editingId}
              />

              <div>
                <Label>Imagen del producto</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    className="h-11 flex-1 bg-neutral-50 text-neutral-500"
                    value={form.image_url}
                    placeholder="Sube una imagen…"
                    disabled
                    data-testid="product-image"
                  />
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProductImageUpload}
                    className="hidden"
                    data-testid="product-image-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="h-11 shrink-0"
                    data-testid="product-image-upload-btn"
                  >
                    <Upload size={16} className="mr-2" />
                    {uploadingImage ? "Subiendo…" : "Subir"}
                  </Button>
                  {form.image_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      className="h-11 shrink-0"
                      data-testid="product-image-clear-btn"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
                {form.image_url && (
                  <div className="mt-2">
                    <img
                      src={form.image_url}
                      alt=""
                      className="h-20 w-20 rounded-lg object-cover bg-neutral-100 border border-neutral-200"
                      data-testid="product-image-preview"
                    />
                  </div>
                )}
                <p className="text-[11px] text-neutral-500 mt-1">JPG, PNG o WebP · máx 3MB</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="product-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="h-4 w-4 accent-[color:var(--company-primary)]"
                  data-testid="product-active"
                />
                <label htmlFor="product-active" className="text-sm">Producto activo (visible para clientes)</label>
              </div>
            </div>
            </div>

            {/* Footer sticky con botones */}
            <div className="border-t border-neutral-200 bg-white px-4 sm:px-6 py-3 flex gap-2 shrink-0">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} className="flex-1 h-11">
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="flex-1 h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                data-testid="save-product-btn"
              >
                {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear producto"}
              </Button>
            </div>
          </div>
        </BottomSheet>

        {/* BottomSheet: Editar canal (imagen + visibilidad) */}
        <BottomSheet
          open={!!channelEdit}
          onOpenChange={(o) => { if (!o) setChannelEdit(null); }}
          title={channelEdit ? `Editar canal · ${channelEdit.label}` : "Editar canal"}
          description="Define la imagen que verá el cliente y si este canal está visible."
          testid="channel-edit-sheet"
        >
          {channelEdit && (
            <div className="flex flex-col h-[55vh]">
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 overflow-y-auto flex-1 space-y-5">
                <div>
                  <Label>Imagen del canal</Label>
                  <p className="text-[11px] text-neutral-500 mt-0.5 mb-2">Aparecerá en el botón "{channelEdit.label}" del cliente. Si la dejas vacía, se usa el logo de la empresa.</p>
                  <div className="flex gap-2">
                    <Input
                      className="h-11 flex-1 bg-neutral-50 text-neutral-500"
                      value={channelEdit.image_url}
                      placeholder="Sube una imagen…"
                      disabled
                      data-testid="channel-image-url"
                    />
                    <input
                      ref={channelImgInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleChannelImgUpload}
                      className="hidden"
                      data-testid="channel-image-file"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => channelImgInputRef.current?.click()}
                      disabled={uploadingChannelImg}
                      className="h-11 shrink-0"
                      data-testid="channel-image-upload-btn"
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingChannelImg ? "Subiendo…" : "Subir"}
                    </Button>
                    {channelEdit.image_url && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setChannelEdit({ ...channelEdit, image_url: "" })}
                        className="h-11 shrink-0"
                        data-testid="channel-image-clear-btn"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                  {channelEdit.image_url && (
                    <div className="mt-3">
                      <img
                        src={channelEdit.image_url}
                        alt=""
                        className="h-28 w-full max-w-md rounded-xl object-cover bg-neutral-100 border border-neutral-200"
                        data-testid="channel-image-preview"
                      />
                    </div>
                  )}
                  <p className="text-[11px] text-neutral-500 mt-2">JPG, PNG o WebP · máx 3MB</p>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-neutral-200">
                  <input
                    id="channel-visible"
                    type="checkbox"
                    checked={!channelEdit.hidden}
                    onChange={(e) => setChannelEdit({ ...channelEdit, hidden: !e.target.checked })}
                    className="h-4 w-4 accent-[color:var(--company-primary)]"
                    data-testid="channel-visible-toggle"
                  />
                  <label htmlFor="channel-visible" className="text-sm">
                    Visible para los clientes
                  </label>
                </div>
                <p className="text-[11px] text-neutral-500 -mt-3 ml-6">
                  Si lo desactivas, el botón "{channelEdit.label}" no aparece en el home del cliente.
                </p>
              </div>
              <div className="border-t border-neutral-200 bg-white px-4 sm:px-6 py-3 flex gap-2 shrink-0">
                <Button variant="outline" onClick={() => setChannelEdit(null)} disabled={savingChannel} className="flex-1 h-11">
                  Cancelar
                </Button>
                <Button
                  onClick={saveChannel}
                  disabled={savingChannel}
                  className="flex-1 h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                  data-testid="save-channel-btn"
                >
                  {savingChannel ? "Guardando…" : "Guardar cambios"}
                </Button>
              </div>
            </div>
          )}
        </BottomSheet>
      </main>
    </div>
  );
}
